import { useCallback } from 'react';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { contactPanelTaskListTrackingIds, PaginatedTaskList, TaskListFilters } from '@frontend/task-center-components';
import { ContactTaskListStoreProvider, useTaskCenterStore } from '@frontend/task-center-scope';
import { theme } from '@frontend/theme';
import { Button, Heading, Info } from '@frontend/design-system';

interface Props {
  contactId: string;
}

const DEFAULT_TASK_STATUSES_FOR_CONTACT_PANEL = [
  TaskCenterTypes.TaskStatus.STATUS_TODO,
  TaskCenterTypes.TaskStatus.STATUS_IN_PROGRESS,
];

const ContactTaskTabContentInner = ({ contactId }: Props) => {
  const { t } = useTranslation('contacts');
  const tokens = useTranslationTokens();
  const { setTaskMode, setIsTaskCenterOpen, isTaskCenterOpen, setSelectedTask } = useTaskCenterStore([
    'setTaskMode',
    'setIsTaskCenterOpen',
    'isTaskCenterOpen',
    'setSelectedTask',
  ]);

  const onCreateTask = useCallback(() => {
    setSelectedTask({
      patient: { id: contactId, name: '' },
      title: '',
      locationId: '',
      type: TaskCenterTypes.TaskType.TYPE_UNKNOWN,
      source: TaskCenterTypes.TaskSource.SOURCE_UNKNOWN,
    });
    setTaskMode('create');
    if (!isTaskCenterOpen) {
      setIsTaskCenterOpen(true);
    }
  }, [setSelectedTask, contactId, setTaskMode, isTaskCenterOpen, setIsTaskCenterOpen]);

  return (
    <section css={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <header
        css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: theme.spacing(1, 2) }}
      >
        <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <Heading level={3} css={{ fontSize: theme.font.size.large }}>
            {t('Tasks')}
          </Heading>
          <Info placement='bottom-start' trigger='hover'>
            {t('Showing active tasks for this {{contact}}. Navigate to Task Center for completed tasks.', {
              contact: tokens.individualContact.toLowerCase(),
            })}
          </Info>
        </div>
        <div css={{ display: 'flex', gap: theme.spacing(1) }}>
          <Button
            variant='secondary'
            iconName='draft'
            size='medium'
            label={t('Create Task')}
            hoverLabel={t('Create Task')}
            trackingId={contactPanelTaskListTrackingIds.createTaskDraftButton}
            onClick={onCreateTask}
          />
          <TaskListFilters fromContactPanel />
        </div>
      </header>

      <PaginatedTaskList
        statuses={DEFAULT_TASK_STATUSES_FOR_CONTACT_PANEL}
        listQueryEnabled={Boolean(contactId)}
        onCreateTask={onCreateTask}
        patientId={contactId}
      />
    </section>
  );
};

export const ContactTaskTabContent = ({ contactId }: Props) => {
  return (
    <ContactTaskListStoreProvider>
      <ContactTaskTabContentInner contactId={contactId} />
    </ContactTaskListStoreProvider>
  );
};
